const getScopeFilterStorageKey = (scopeType: string | null) => `scopefilter.${scopeType}`;

const getRecentlySelectedScopeStorageKey = (scopeType: string | null) => `recentlySelectedScope.${scopeType}`;

/**
 * Takes a String or Array of strings and saves it to localStorage.
 *
 * @param {string} scopeType - Name of the scopeType, usually `Facility`
 * @param {string | string[]} newScopeFilter - A string for an scope/facility or an array of strings describing the scopes.
 */
export function setScopeFilter(scopeType: string | null, newScopeFilter: string | string[]) {
    if (typeof newScopeFilter === 'string') {
        newScopeFilter = [newScopeFilter];
    }

    const storageKey = getScopeFilterStorageKey(scopeType);
    writeLocalStorage(storageKey, newScopeFilter);
}

/**
 * Takes a String or Array of strings and saves it to localStorage.
 *
 * @param {string} scopeType - Name of the scopeType, usually `Facility`
 * @returns {string[]} - An array of strings (scope/facility)
 */
export function getScopeFilter(scopeType: string | null): string[] {
    const storageKey = getScopeFilterStorageKey(scopeType);
    const scopeFilter = readLocalStorage(storageKey, []);

    if (scopeFilter.length !== 0) {
        scopeFilter.sort();
    }

    return scopeFilter;
}

/**
 * Fetches a string from sessionStorage stored under the key `recentlySelectedScope.Facility`.
 *
 * @param {string} scopeType - Name of the scopeType, usually `Facility`
 * @returns {string | null} - Name of the scope, e.g.`AHA`
 */
export function getRecentlySelectedScope(scopeType: string | null): string | null {
    const storageKey = getRecentlySelectedScopeStorageKey(scopeType);
    const value = readSessionStorage(storageKey, null, true);
    return value === 'null' ? null : value;
}

/**
 * Takes a ScopeName String and saves it to sessionStorage.
 *
 * @param {string} scopeType - Name of the scopeType, usually `Facility`
 * @param {string} scope - Name of the scope, e.g.`AHA`
 */
export function setRecentlySelectedScope(scopeType: string | null, scope: string | null) {
    const storageKey = getRecentlySelectedScopeStorageKey(scopeType);
    writeSessionStorage(storageKey, scope, true);
}

function readLocalStorage<T>(storageKey: string, defaultReturn: T): T {
    return JSON.parse(localStorage.getItem(storageKey) ?? 'null') ?? defaultReturn;
}

function writeLocalStorage(storageKey: string, storageValue: string[]) {
    localStorage.setItem(storageKey, JSON.stringify(storageValue));
}

function readSessionStorage(storageKey: string, defaultReturn: string | null, raw = false): string | null {
    return raw
        ? sessionStorage.getItem(storageKey) ?? defaultReturn
        : JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') ?? defaultReturn;
}

function writeSessionStorage(storageKey: string, storageValue: string | null, raw = false) {
    if (raw) {
        sessionStorage.setItem(storageKey, storageValue ?? 'null');
    } else {
        sessionStorage.setItem(storageKey, JSON.stringify(storageValue));
    }
}
